import type {
  IssueTemplateFilteringDtoOptions,
  IssueTemplateSortingDtoOptions,
  UserDto,
} from '@issuemanager/api-client';
import { getSortingModelByField } from '@issuemanager/apps/client/components';
import { useDateTimeFormat } from '@issuemanager/apps/client/hooks';
import { getOptionalPlatformISOString } from '@issuemanager/apps/client/utils';
import type { GridSortModel } from '@mui/x-data-grid-pro';
import {
  ISSUE_TEMPLATE_FIELD_ADDED_BY,
  ISSUE_TEMPLATE_FIELD_CREATED,
  ISSUE_TEMPLATE_FIELD_IS_AUTOMATIC,
  ISSUE_TEMPLATE_FIELD_ISSUE_SUBTYPE,
  ISSUE_TEMPLATE_FIELD_ISSUE_TYPE,
  ISSUE_TEMPLATE_FIELD_SEVERITY,
  ISSUE_TEMPLATE_FIELD_TITLE,
} from './constants';
import type { IssueTemplateGridFilterModel } from './types';

export function useIssueTemplateGridQueryParams(
  filterModel: IssueTemplateGridFilterModel,
  sortModel: GridSortModel,
): {
  filter: IssueTemplateFilteringDtoOptions;
  sort: IssueTemplateSortingDtoOptions;
} {
  return {
    filter: useIssueTemplateGridQueryFilterParam(filterModel),
    sort: useIssueTemplateGridQuerySortParam(sortModel),
  };
}

function useIssueTemplateGridQueryFilterParam(
  filterModel: IssueTemplateGridFilterModel,
): IssueTemplateFilteringDtoOptions {
  const { timezoneOffset } = useDateTimeFormat();

  const issueTypeFilter = filterModel[ISSUE_TEMPLATE_FIELD_ISSUE_TYPE];
  const issueSubtypeFilter = filterModel[ISSUE_TEMPLATE_FIELD_ISSUE_SUBTYPE];
  const addedByFilter = filterModel[ISSUE_TEMPLATE_FIELD_ADDED_BY];
  const severityFilter = filterModel[ISSUE_TEMPLATE_FIELD_SEVERITY];
  const createdAtFilter = filterModel[ISSUE_TEMPLATE_FIELD_CREATED];

  const addedBy = addedByFilter?.map((user: UserDto) => user.id);
  const severity = severityFilter?.map((status) => {
    if (status.value === null) {
      return 'null';
    }
    return status.value;
  });
  const issueType = issueTypeFilter?.map((it) => it.name);
  const issueSubtype = issueSubtypeFilter?.map((is) => is.id);
  const createdAtRangeStart = getOptionalPlatformISOString(
    createdAtFilter?.[0],
    timezoneOffset,
  );
  const createdAtRangeEnd = getOptionalPlatformISOString(
    createdAtFilter?.[1],
    timezoneOffset,
  );

  return {
    issueType,
    issueSubtype,
    severity,
    addedBy,
    createdAtRangeStart,
    createdAtRangeEnd,
  };
}

function useIssueTemplateGridQuerySortParam(
  sortModel: GridSortModel,
): IssueTemplateSortingDtoOptions {
  const title = getSortingModelByField(sortModel, ISSUE_TEMPLATE_FIELD_TITLE);
  const issueType = getSortingModelByField(
    sortModel,
    ISSUE_TEMPLATE_FIELD_ISSUE_TYPE,
  );
  const issueSubtype = getSortingModelByField(
    sortModel,
    ISSUE_TEMPLATE_FIELD_ISSUE_SUBTYPE,
  );
  const addedBy = getSortingModelByField(
    sortModel,
    ISSUE_TEMPLATE_FIELD_ADDED_BY,
  );
  const severity = getSortingModelByField(
    sortModel,
    ISSUE_TEMPLATE_FIELD_SEVERITY,
  );
  const createdAt = getSortingModelByField(
    sortModel,
    ISSUE_TEMPLATE_FIELD_CREATED,
  );
  const isAutomatic = getSortingModelByField(
    sortModel,
    ISSUE_TEMPLATE_FIELD_IS_AUTOMATIC,
  );

  return {
    title,
    issueType,
    issueSubtype,
    addedBy,
    createdAt,
    severity,
    isAutomatic,
  };
}
